import React, { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { store, type RootState } from "../../redux/store";
import { BACKEND_URL } from "../../Apis/auth";
import { connectSocket, offSocket, onSocket } from "../../services/socket";
import type { PriorityNotificationEvent } from "../../types/socketEvents";
import {
  addNotification,
  markReadLocally,
  markAllReadLocally,
  updateAggregated,
  fetchUnreadCount,
  fetchNotifications,
  removeNotification,
} from "../../redux/slices/NotificationsSlice";
import { requestOpportunitiesRefetch } from "../../redux/slices/OpportunitiesSlice";
import {
  extractNotificationFromSocketPayload,
  isExportCompletionNotification,
  isWorkOrderDueNotification,
} from "../../utils/notificationSocketPayload";
import { localExportNotificationId } from "../../utils/buildExportCompletionNotification";
import {
  isRefreshRequiredPayload,
  showLiveNotificationToast,
  toastNewNotifications,
} from "../../utils/notificationLiveRefresh";
import { emitLeadCountsRefresh } from "../../hooks/useDispositionRealtime";
import type { AppNotification } from "../../types/notifications";

/**
 * GlobalSocketNotifications
 *
 * Mounted once in the authenticated app shell.
 * Listens to socket "notification" events and shows toasts for
 * work order reminder priority activations, regardless of the current page.
 *
 * CAR-2738: `notification:new` is often `{ refreshRequired: true }` only.
 * Refetch GET /notifications and toast newly arrived unread items once.
 */
const GlobalSocketNotifications: React.FC = () => {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(
    (state: RootState) => state.auth.accessToken
  );
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    if (!accessToken) return;

    connectSocket(accessToken, {
      serverUrl: BACKEND_URL,
      maxReconnectAttempts: 5,
      reconnectDelay: 2000,
    });

    dispatch(fetchUnreadCount());

    const handleConnect = () => {
      dispatch(fetchUnreadCount());
    };

    const refreshMyLeadsForWorkOrderPriority = () => {
      dispatch(requestOpportunitiesRefetch());
      emitLeadCountsRefresh();
    };

    const ingestNotification = (notif: AppNotification) => {
      const existingItems =
        (store.getState() as RootState).notifications?.items ?? [];
      if (existingItems.some((item) => item._id === notif._id)) {
        showLiveNotificationToast(notif);
        return;
      }
      const alreadyHasExportNotice =
        isExportCompletionNotification(notif) &&
        existingItems.some(
          (item) =>
            item.entityId === notif.entityId &&
            isExportCompletionNotification(item)
        );

      if (alreadyHasExportNotice) {
        dispatch(removeNotification(localExportNotificationId(notif.entityId)));
      }

      dispatch(addNotification(notif));
      dispatch(fetchUnreadCount());

      if (isWorkOrderDueNotification(notif)) {
        refreshMyLeadsForWorkOrderPriority();
      }

      if (alreadyHasExportNotice) return;
      showLiveNotificationToast(notif);
    };

    const refreshFromInvalidation = async () => {
      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;
      const previousIds = new Set(
        ((store.getState() as RootState).notifications?.items ?? []).map(
          (item) => item._id
        )
      );
      try {
        await Promise.all([
          dispatch(fetchNotifications({ reset: true })).unwrap(),
          dispatch(fetchUnreadCount()).unwrap(),
        ]);
        const items =
          (store.getState() as RootState).notifications?.items ?? [];
        toastNewNotifications(items, previousIds).forEach((item) => {
          if (isWorkOrderDueNotification(item)) {
            refreshMyLeadsForWorkOrderPriority();
          }
        });
      } catch {
        // fail-open: drawer still loads on open
      } finally {
        refreshInFlightRef.current = false;
      }
    };

    const handleNotification = (data: PriorityNotificationEvent | any) => {
      try {
        if (data?.type === "workorder:priority:activated" && data?.message) {
          toast.info(data.message, {
            duration: 1800,
          });
          refreshMyLeadsForWorkOrderPriority();
        }
      } catch (err) {
        console.error(
          "[GlobalSocketNotifications] Error handling notification:",
          err
        );
      }
    };

    const handleNotificationNew = (data: unknown) => {
      try {
        const notif = extractNotificationFromSocketPayload(data);
        if (notif) {
          ingestNotification(notif);
          return;
        }
        if (isRefreshRequiredPayload(data)) {
          void refreshFromInvalidation();
        }
      } catch (err) {
        console.error(
          "[GlobalSocketNotifications] Error handling notification:new:",
          err
        );
      }
    };

    const handleNotificationRead = (data: { notificationId: string }) => {
      try {
        if (data?.notificationId)
          dispatch(markReadLocally(data.notificationId));
      } catch (err) {
        console.error(
          "[GlobalSocketNotifications] Error handling notification:read:",
          err
        );
      }
    };

    const handleNotificationReadAll = () => {
      try {
        dispatch(markAllReadLocally());
      } catch (err) {
        console.error(
          "[GlobalSocketNotifications] Error handling notification:read-all:",
          err
        );
      }
    };

    const handleNotificationAggregated = (data: {
      notificationId: string;
      aggregationCount: number;
      title: string;
    }) => {
      try {
        if (data?.notificationId) dispatch(updateAggregated(data));
      } catch (err) {
        console.error(
          "[GlobalSocketNotifications] Error handling notification:aggregated:",
          err
        );
      }
    };

    onSocket("connect", handleConnect);
    onSocket("notification", handleNotification);
    onSocket("notification:new", handleNotificationNew);
    onSocket("notification:read", handleNotificationRead);
    onSocket("notification:read-all", handleNotificationReadAll);
    onSocket("notification:aggregated", handleNotificationAggregated);
    onSocket(
      "workorder:priority:activated",
      refreshMyLeadsForWorkOrderPriority
    );
    onSocket("workorder:priority:cleared", refreshMyLeadsForWorkOrderPriority);

    return () => {
      offSocket("connect", handleConnect);
      offSocket("notification", handleNotification);
      offSocket("notification:new", handleNotificationNew);
      offSocket("notification:read", handleNotificationRead);
      offSocket("notification:read-all", handleNotificationReadAll);
      offSocket("notification:aggregated", handleNotificationAggregated);
      offSocket(
        "workorder:priority:activated",
        refreshMyLeadsForWorkOrderPriority
      );
      offSocket(
        "workorder:priority:cleared",
        refreshMyLeadsForWorkOrderPriority
      );
    };
  }, [accessToken, dispatch]);

  return null;
};

export default GlobalSocketNotifications;
